import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppLayoutOptional } from '../../../contexts/AppLayoutContext';
import NativeBootScreen, { type NativeBootPhase } from '../NativeBootScreen/NativeBootScreen';
import { hideNativeSplashWithFade } from '../../../native/nativeSplash';
import {
  markNativeBootCompleted,
  NATIVE_BOOT_EXIT_MS,
  shouldDismissNativeBoot,
  shouldShowNativeBoot,
  type NativeBootGatePhase,
} from '../../../native/nativeBootTiming';

interface NativeBootGateProps {
  children: React.ReactNode;
}

/**
 * Overlay de boot natif au cold start.
 * L'app monte en arrière-plan ; le splash Capacitor cède la place à l'overlay React
 * puis disparaît en fondu quand tout est prêt.
 */
const NativeBootGate: React.FC<NativeBootGateProps> = ({ children }) => {
  const { isNative } = useAppLayoutOptional();
  const { isAuthReady } = useAuth();
  const showBoot = shouldShowNativeBoot(isNative);

  const [gatePhase, setGatePhase] = useState<NativeBootGatePhase>(showBoot ? 'boot' : 'done');
  const bootStartRef = useRef(showBoot ? performance.now() : 0);
  const splashHiddenRef = useRef(false);
  const dismissStartedRef = useRef(false);

  const hideSplashOnce = useCallback(() => {
    if (splashHiddenRef.current) return;
    splashHiddenRef.current = true;
    void hideNativeSplashWithFade();
  }, []);

  /* Masquer le splash natif dès que l'overlay React est peint (handoff sans flash). */
  useEffect(() => {
    if (!showBoot || gatePhase === 'done') return;

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        hideSplashOnce();
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [showBoot, gatePhase, hideSplashOnce]);

  /* Boucle de décision : auth prête + durée min, ou timeout max. */
  useEffect(() => {
    if (!showBoot || gatePhase !== 'boot') return;

    const tick = () => {
      if (dismissStartedRef.current) return;

      const elapsedMs = performance.now() - bootStartRef.current;
      if (shouldDismissNativeBoot({ isAuthReady, elapsedMs })) {
        dismissStartedRef.current = true;
        setGatePhase('exit');
        window.setTimeout(() => {
          const durationMs = Math.round(performance.now() - bootStartRef.current);
          markNativeBootCompleted();
          setGatePhase('done');
          void import('../../../native/nativeAnalytics').then((m) =>
            m.trackNativeColdStart(durationMs),
          );
        }, NATIVE_BOOT_EXIT_MS);
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 80);
    return () => window.clearInterval(intervalId);
  }, [showBoot, gatePhase, isAuthReady]);

  const screenPhase: NativeBootPhase =
    gatePhase === 'exit' ? 'exit' : gatePhase === 'boot' ? 'enter' : 'exit';

  return (
    <>
      {children}
      {showBoot && gatePhase !== 'done' ? <NativeBootScreen phase={screenPhase} /> : null}
    </>
  );
};

export default NativeBootGate;
